import { Component, Input, Output, EventEmitter } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

/**
 * Banner component that prompts users to set up metadata for legacy projects.
 *
 * Displays when a project doesn't have project-metadata.json file.
 * Shows a prominent banner with "Set Up Metadata" action.
 * Can be dismissed temporarily but reappears until metadata is created.
 *
 * Part of Issue #227 - Project Metadata Management Phase 1.
 */
@Component({
  selector: 'app-legacy-project-banner',
  imports: [MatCardModule, MatButtonModule, MatIconModule],
  template: `
    @if (show && !dismissed) {
      <mat-card class="legacy-project-banner">
        <mat-card-content>
          <div class="banner-content">
            <mat-icon class="banner-icon">info</mat-icon>
            <div class="banner-text">
              <h3>Set Up Project Metadata</h3>
              <p>This project doesn't have metadata yet. Add a project name and description to get started.</p>
            </div>
            <div class="banner-actions">
              <button mat-raised-button color="primary" (click)="onSetupClick()">
                <mat-icon>settings</mat-icon>
                Set Up Metadata
              </button>
              <button mat-button (click)="onDismiss()">Dismiss</button>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    }
  `,
  styles: [
    `
      .legacy-project-banner {
        margin-bottom: 24px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
      }

      .legacy-project-banner ::ng-deep .mat-mdc-card-content {
        padding: 16px 24px;
      }

      .banner-content {
        display: flex;
        align-items: center;
        gap: 16px;
      }

      .banner-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
        flex-shrink: 0;
      }

      .banner-text {
        flex: 1;
      }

      .banner-text h3 {
        margin: 0 0 8px 0;
        font-size: 20px;
        font-weight: 500;
      }

      .banner-text p {
        margin: 0;
        opacity: 0.9;
        font-size: 14px;
      }

      .banner-actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;
      }

      .banner-actions button {
        color: white;
      }

      .banner-actions .mat-mdc-raised-button {
        background-color: rgba(255, 255, 255, 0.2);
      }

      .banner-actions .mat-mdc-raised-button:hover {
        background-color: rgba(255, 255, 255, 0.3);
      }
    `,
  ],
})
export class LegacyProjectBannerComponent {
  /**
   * Whether to show the banner (true if project has no metadata)
   */
  @Input() show: boolean = false;

  /**
   * Emits when user clicks "Set Up Metadata"
   */
  @Output() setupClick = new EventEmitter<void>();

  /**
   * Emits when user dismisses the banner
   */
  @Output() dismiss = new EventEmitter<void>();

  /**
   * Whether banner is dismissed (temporary, reappears on reload)
   */
  dismissed: boolean = false;

  onSetupClick(): void {
    this.setupClick.emit();
  }

  onDismiss(): void {
    this.dismissed = true;
    this.dismiss.emit();
  }
}
